import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { isEqual } from 'lodash';

import ValidationAlert from '../../../app/components/ValidationAlert';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useAuth } from '../../../common/hooks/useAuth';
import {
  CreateUpdateSurveyDto,
  GetSurveyDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  newSurvey,
  convertSurvey,
} from '../../../common/pokCore/contexts/SurveyContext';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { SurveyEditorTabs } from '../../containers/SurveyEditorView/SurveyEditorView';

import { SurveyBasicDataEditor } from './SurveyBasicDataEditor';
import { SurveyBasicDataControls } from './SurveyBasicDataControls';
interface SurveyBasicDataProps {
  survey?: GetSurveyDto;
  surveyId?: string;
  setSurveyId: Dispatch<SetStateAction<string | undefined>>;
  setNotSavedTab: (tab: SurveyEditorTabs | undefined) => void;
  setActiveTab: Dispatch<SetStateAction<string>>;
  refreshSurvey: () => Promise<void>;
  isLocked?: boolean;
  isOtherTabInEditMode?: boolean;
}

export const SurveyBasicData: React.FC<SurveyBasicDataProps> = props => {
  const auth = useAuth();
  const [survey, setSurvey] = useState<CreateUpdateSurveyDto>(
    newSurvey(auth.currentUser?.sub),
  );
  const [originalSurvey, setOriginalSurvey] =
    useState<CreateUpdateSurveyDto>(survey);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { t, tk } = useTranslation('administrationLng');
  const pok = usePokCore();

  useEffect(() => {
    if (props.survey) {
      const converted = convertSurvey(props.survey);
      setSurvey(converted);
      setOriginalSurvey(converted);
    }
  }, [props.survey]);

  const readonly = props.isLocked || props.isOtherTabInEditMode;

  const propertyChange = (obj: Partial<CreateUpdateSurveyDto>) => {
    const updatedSurvey = {
      ...survey,
      ...obj,
    };

    const dataChanged = !isEqual(updatedSurvey, originalSurvey);
    props.setNotSavedTab(dataChanged ? SurveyEditorTabs.BASIC_DATA : undefined);

    setSurvey(updatedSurvey);
  };

  return (
    <>
      <Form className="d-grid gap-4 mb-3">
        <SurveyBasicDataEditor
          survey={survey}
          propertyChange={propertyChange}
          contactTypeShortname={props.survey?.contactType?.shortname}
          isLocked={readonly}
        />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
        {props.isLocked && (
          <Alert variant="warning" className="m-3">
            {t(tk.survey.surveyLockedInfo)}
          </Alert>
        )}
        <div className="d-flex">
          {props.surveyId && (
            <LastEditorInfo
              id={props.surveyId}
              className="color-primary"
              method={pok.surveys.getLastEditor}
              additionalText={t(tk.common.lastModification)}
            />
          )}
          <SurveyBasicDataControls
            survey={survey}
            originalSurvey={originalSurvey}
            surveyId={props.surveyId}
            setSurveyId={props.setSurveyId}
            setNotSavedTab={props.setNotSavedTab}
            setSaveErrors={setSaveErrors}
            setShowSaveErrors={setShowSaveErrors}
            setOriginalSurvey={setOriginalSurvey}
            setActiveTab={props.setActiveTab}
            refreshSurvey={props.refreshSurvey}
          />
        </div>
      </Form>
    </>
  );
};
